// src/pages/Company.tsx

import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import InquireComponent from "../components/Inquire.tsx";
import MobileCompanyList from "../components/MobileCompanyList.tsx";
import Marquee from "react-fast-marquee";

const Container = styled.div`
  width: auto;
  height: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 1550px) {
    width: 100%;
  }
`;

const CustomContainer = styled.div<{
  padding?: string;
  marginTop?: string;
  width?: string;
  height?: string;
  backgroundColor?: string;
  borderRadius?: string;
  display?: string;
  alignItems?: string;
  justifyContent?: string;
  flexDirection?: string;
  mobileFlexDirection?: string;
  gap?: string;
  border?: string;

  // 반응형 옵션
  mobilePadding?: string;
  mobileMargin?: string;
  mobileWidth?: string;
  mobileHeight?: string;
  mobileBackgroundColor?: string;
  mobileBorderRadius?: string;
  mobileGap?: string;
}>`
  display: ${({ display = "flex" }) => display};
  padding: ${({ padding = "0px 0px 0px 0px" }) => padding};
  margin-top: ${({ marginTop = "0" }) => marginTop};
  width: ${({ width = "100%" }) => width};
  height: ${({ height = "auto" }) => height};
  background-color: ${({ backgroundColor = "transparent" }) => backgroundColor};
  border-radius: ${({ borderRadius = "0" }) => borderRadius};
  border: ${({ border = "none" }) => border};
  align-items: ${({ alignItems = "center" }) => alignItems};
  justify-content: ${({ justifyContent = "center" }) => justifyContent};
  flex-direction: ${({ flexDirection = "row" }) => flexDirection};
  gap: ${({ gap = "0" }) => gap};
  box-sizing: border-box;

  @media (max-width: 1550px) {
    padding: ${({ mobilePadding }) => mobilePadding || "inherit"};
    margin: ${({ mobileMargin }) => mobileMargin || "inherit"};
    width: ${({ mobileWidth }) => mobileWidth || "inherit"};
    height: ${({ mobileHeight }) => mobileHeight || "inherit"};
    background-color: ${({ mobileBackgroundColor }) =>
      mobileBackgroundColor || "inherit"};
    border-radius: ${({ mobileBorderRadius }) =>
      mobileBorderRadius || "inherit"};
    flex-direction: ${({ mobileFlexDirection }) =>
      mobileFlexDirection || "inherit"};
    gap: ${({ mobileGap }) => mobileGap || "inherit"};
  }
`;

const IntroTextWrapper = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
`;

const Title = styled.div<{ marginTop: string }>`
  display: flex;
  margin-top: ${({ marginTop }) => marginTop || "0"};
  font-size: 32px;
  color: white;
  font-weight: bold;
  text-align: center;
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: 1550px) {
    font-size: 26px;
    margin-top: 30px;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const TitleLight = styled.div`
  display: flex;
  margin-top: ${({ marginTop }) => marginTop || "0"};
  font-size: 32px;
  color: white;
  font-weight: Regular;
  text-align: center;

  @media (max-width: 1550px) {
    font-size: 26px;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const Subtitle = styled.div`
  display: flex;
  font-size: 18px;
  margin-top: ${({ marginTop }) => marginTop || "0"};
  color: white;

  @media (max-width: 1550px) {
    font-size: 15px;
    white-space: pre-wrap;
    word-break: break-word;
    text-align: center;
  }
`;

const Image = styled.img`
  margin-top: 50px; // 이미지와 텍스트 간격
  margin-bottom: 30px;
  width: 100%; // 이미지 크기 (원하는 비율로 조정 가능)
  max-width: 878px; // 최대 크기 설정
  height: auto; // 비율에 맞게 높이 자동 조정

  @media (max-width: 1550px) {
    margin-top: 30px;
    max-width: 334px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  align-items: center;

  @media (max-width: 1550px) {
    flex-direction: column;
  }
`;

// 버튼 스타일 설정
const Button = styled.button`
  background-color: white; // 버튼 배경 색
  color: black; // 버튼 텍스트 색
  font-size: 26px; // 텍스트 크기
  font-weight: 600;
  font-family: "Pretendard";
  padding: 12px 20px; // 여백
  border: 1px; // 테두리 없음
  border-color: #2051ad;
  border-radius: 100px; // 둥근 모서리
  box-sizing: border-box;
  cursor: pointer; // 마우스 커서가 버튼 위에 있을 때 손 모양
  margin: 30px; // 버튼 간의 간격
  width: 270px;
  height: 65px;

  &:hover,
  &:active {
    background-color: #e4eeff; // 마우스를 올리거나 클릭했을 때 배경 색 변경
    border: 2px solid white;
    color: #2051ad;
    border-color: #2051ad; // 마우스를 올리거나 클릭했을 때 테두리 굵기 변경
  }

  &:focus {
    outline: none; // 버튼 클릭 시 테두리 없애기
  }

  @media (max-width: 1550px) {
    width: 200px;
    height: 50px;
    gap: 10px;
    font-size: 20px;
    margin: 10px;
  }
`;

const CardContainer = styled.div`
  display: flex; // flexbox로 가로로 배치
  justify-content: space-between; // 카드들 사이의 간격을 동일하게
  align-items: start;
  gap: 20px; // 카드 간의 간격 설정
  margin-top: 40px;
  padding: 0 20px; // 카드들 양쪽에 여백 추가

  @media (max-width: 1550px) {
    margin-top: 20px;
    flex-direction: column; // 화면이 작을 때 세로로 배치
    align-items: center;
  }
`;

const Card = styled.div<{ bgColor: string; textColor: string }>`
  width: 318px; // 카드의 너비 설정
  height: 268px; // 카드의 높이 설정
  background-color: ${(props) => props.bgColor}; // 배경색을 props로 설정
  color: ${(props) => props.textColor}; // 폰트색을 props로 설정
  border-radius: 8px; // 둥근 모서리
  padding: 20px;
  display: flex;
  flex-direction: column; // 아이콘과 텍스트 세로로 배치
  align-items: center; // 가운데 정렬
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); // 그림자 효과
  font-family: "Pretendard"; // 폰트 설정
  border: 0.5px solid;
  border-color: #adc7da;
  box-sizing: border-box;

  @media (max-width: 1550px) {
    width: 300px;
    height: 190px;
  }
`;

const Icon = styled.img<{
  width: string;
  height: string;
  marginBottom?: string;
  borderRadius?: string;
  border?: string;
  mobileWidth?: string;
  mobileHeight?: string;
  mobileMargin?: string;
}>`
  width: ${(props) => props.width}; // width 설정
  height: ${(props) => props.height}; // height 설정
  margin-bottom: ${(props) => props.marginBottom}; // height 설정
  border-radius: ${(props) => props.borderRadius}; // border-radius 설정
  border: ${(props) => props.border}; // border 설정
  @media (max-width: 1550px) {
    margin-bottom: 5px;
    height: ${(props) => props.mobileHeight};
    width: ${(props) => props.mobileWidth};
    margin: ${(props) => props.mobileMargin};
  }
`;
// 요약글 스타일 설정 (font-size, font-weight, marginTop을 props로 받음)
const Summary = styled.div<{
  fontSize: string;
  fontWeight: string;
  marginTop?: string;
}>`
  font-size: ${(props) => props.fontSize}; // 폰트 크기 설정
  font-weight: ${(props) => props.fontWeight}; // 폰트 두께 설정
  text-align: center; // 텍스트 가운데 정렬
  margin-top: ${(props) =>
    props.marginTop || "0"}; // marginTop 설정, 기본값은 0

  @media (max-width: 1550px) {
    font-size: 15px;
  }
`;

const SpanWrapper = styled.div<{ marginTop?: string; marginBottom?: string }>`
  height: auto;
  margin-top: ${({ marginTop }) => marginTop || "150px"};
  margin-bottom: ${({ marginBottom }) => marginBottom || "50px"};
  display: flex; // 텍스트 조각들을 가로로 배치
  justify-content: center;
  align-items: center;
  box-sizing: border-box;

  @media (max-width: 1550px) {
    margin-top: 30px;
  }
`;

const SpanText = styled.div<{
  fontSize: string;
  color?: string;
}>`
  font-size: ${(props) => props.fontSize};
  margin-right: 10px; // 텍스트 간의 간격
  font-family: "Pretendard";
  color: ${({ color }) => color || "white"};
  font-weight: regular;
  white-space: pre-line;
  text-align: center;

  @media screen and (max-width: 1550px) {
    font-size: 27px;
    white-space: pre-line;
  }
`;

const BoldText = styled.span`
  font-weight: bold;
`;

// 수평 스크롤 애니메이션 정의
const scrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const ScrollingContainer = styled.div`
  display: flex;
  gap: 20px;
  animation: ${scrollAnimation} 60s linear infinite;
  animation-play-state: running; // 기본 동작: 애니메이션 실행
  width: calc(200% + 20px); /* 두 배의 데이터 너비를 처리 */
  &:hover {
    animation-play-state: paused; // 마우스 오버 시 애니메이션 멈춤
  }
`;

const HorizontalCardWrapper = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 20px;
  margin-top: 50px;
  box-sizing: border-box;
  overflow: hidden;
  gap: 20px;
`;

const HorizontalCard = styled.div<{ bgColor: string; textColor: string }>`
  position: relative;
  width: 430px;
  height: 240px;
  background-color: white;
  color: black;
  border-radius: 18px;
  padding-top: 40px;
  padding-left: 30px;
  padding-right: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* justify-content: center; */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  font-family: "Pretendard";
  border: 0.5px solid;
  border-color: #adc7da;
  box-sizing: border-box;
  margin-right: 30px;

  @media (max-width: 375px) {
    width: 333px;
    height: 242px;
    padding-top: 40px;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const DateWrapper = styled.div`
  position: absolute;
  top: -20px; /* 카드의 상단을 벗어나게 설정 */
  left: 50%;
  transform: translateX(-50%);
  width: 164px;
  height: 44px;
  background: linear-gradient(to right, #0073b1, #00e186);
  border-radius: 20.5px; /* 원형 모양 */
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: white;
  font-weight: bold;

  @media (max-width: 1550px) {
    width: 164px;
    height: 44px;
    font-size: 14px;
  }
`;

const LineItem = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  box-sizing: border-box;
  margin-bottom: 10px;
  margin-top: 10px;
`;

const CheckIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 10px;
`;

const LineText = styled.div`
  font-size: 17px;
  color: black;
  font-weight: bold;
`;

const InquireContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 1192px;
  height: 300px;
  margin-top: 100px;
  margin-bottom: 150px;
  justify-content: center; //가로
  align-items: center; //세로
  border-radius: 19px;
  background-color: #00e186;

  @media (max-width: 1550px) {
    width: 333px;
    height: 387px;
  }
`;

const InquireWrapper = styled.div<{
  marginTop?: string;
  marginBottom?: string;
}>`
  height: auto;
  margin-top: ${({ marginTop }) => marginTop || "150px"};
  margin-bottom: ${({ marginBottom }) => marginBottom || "50px"};
  display: flex; // 텍스트 조각들을 가로로 배치
  box-sizing: border-box;

  @media (max-width: 1550px) {
    margin-top: 0px;
  }
`;

const InquireButtonWarpper = styled.div`
  display: flex;
  height: auto;
  justify-content: center;
  align-items: center;

  @media (max-width: 1550px) {
    flex-direction: column;
  }
`;

const InquireButton = styled.button`
  background-color: white; // 버튼 배경 색
  color: black; // 버튼 텍스트 색
  font-size: 18px; // 텍스트 크기
  font-weight: 600;
  font-family: "Pretendard";
  padding: 12px 20px; // 여백
  border: 1px; // 테두리 없음
  border-color: #00be71;
  border-radius: 100px; // 둥근 모서리
  box-sizing: border-box;
  cursor: pointer; // 마우스 커서가 버튼 위에 있을 때 손 모양
  margin: 30px; // 버튼 간의 간격
  width: 220px;
  height: 60px;

  &:hover,
  &:active {
    border: 2px solid white;
    color: #00be71;
    border-color: #00be71; // 마우스를 올리거나 클릭했을 때 테두리 굵기 변경
  }

  &:focus {
    outline: none; // 버튼 클릭 시 테두리 없애기
  }

  @media (max-width: 1550px) {
    width: 200px;
    height: 50px;
    gap: 10px;
    font-size: 20px;
    margin: 10px;
  }
`;

function Company() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1550);
    handleResize(); // 초기 화면 크기 체크
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cardData = [
    {
      bgColor: "#00E186",
      textColor: "black",
      summaryText: "소비자를 꼬시는 센스있는",
      titleText: "마케팅의 고수",
      imgSrc: "/images/company_icon/image1.png", // 이미지 파일 경로 (로컬 경로)
      imgWidth: "103px", // 이미지 너비 설정
      imgHeight: "104px", // 이미지 높이 설정
    },
    {
      bgColor: "#0D9AE5",
      textColor: "white",
      summaryText: "완벽하게 구축하는",
      titleText: "개발의 고수",
      imgSrc: "/images/company_icon/image2.png", // 이미지 파일 경로 (로컬 경로)
      imgWidth: "91px", // 이미지 너비 설정
      imgHeight: "101px", // 이미지 높이 설정
    },
    {
      bgColor: "#00E186",
      textColor: "black",
      summaryText: "트렌디하고 감각적인 컨텐츠를 제작하는",
      titleText: "제작의 고수",
      imgSrc: "/images/company_icon/image3.png", // 이미지 파일 경로 (로컬 경로)
      imgWidth: "76px", // 이미지 너비 설정
      imgHeight: "71px", // 이미지 높이 설정
    },
    {
      bgColor: "#0D9AE5",
      textColor: "white",
      summaryText: "깔끔한 일처리와 센스의",
      titleText: "슬라임 구성원들",
      imgSrc: "/images/company_icon/image4.png", // 이미지 파일 경로 (로컬 경로)
      imgWidth: "87px", // 이미지 너비 설정
      imgHeight: "91px", // 이미지 높이 설정
    },
  ];

  const timelineData = [
    {
      date: "2021.12.01",
      items: [
        "의료 전문 마케팅 사업자 등록",
        "마케팅 서비스를 제공하는 스타트업 기업 출연",
      ],
    },
    {
      date: "2022.03.01",
      items: ["소셜미디어 컨텐츠 마케팅 진행", "의료 광고 집행액 2억원 달성"],
    },
    {
      date: "2023.05.01",
      items: [
        "IT 솔루션 개발 사업체 (주)메타94랩 설립",
        "개발 및 웹 & 앱 서비스 제공 시작",
      ],
    },
    {
      date: "2024.03.01",
      items: [
        "마케팅  사업자/ 개발 사업자 합병 및 사명 (주)슬라임 변경",
        "개발&마케팅 서비스를 제공하고, 국내를 넘은 글로벌 사업체를 목표로 사명 변경",
      ],
    },
    {
      date: "2024.10.01",
      items: ["플랫폼 운영 기획, 서비스 영역 확대"],
    },
  ];

  const extendedData = [...timelineData, ...timelineData];

  const companyIcons1 = [
    "/images/company_icon/company/trinity.png",
    "/images/company_icon/company/honeyddanji.png",
    "/images/company_icon/company/TMS.png",
    "/images/company_icon/company/meta94.png",
    "/images/company_icon/company/vibe.png",
    "/images/company_icon/company/ru.png",
  ];

  const companyIcons2 = [
    "/images/company_icon/company/cabot.png",
    "/images/company_icon/company/lifton.png.png",
    "/images/company_icon/company/sharp.png",
    "/images/company_icon/company/sym.png",
    "/images/company_icon/company/etoile.png",
    "/images/company_icon/company/onyoo.png",
    "/images/company_icon/company/ino.png",
  ];

  const companyIcons3 = [
    "/images/company_icon/company/jungwoo.png",
    "/images/company_icon/company/ete.png",
    "/images/company_icon/company/aging.png",
    "/images/company_icon/company/ssuk.png",
    "/images/company_icon/company/gloty.png",
    "/images/company_icon/company/woori.png",
  ];

  return (
    <Container>
      <IntroTextWrapper>
        <Title marginTop="140px">
          {isMobile
            ? "온라인 마케팅 & \n IT 솔루션 전문 기업 슬라임"
            : "온라인 마케팅 & IT 솔루션 전문 기업 슬라임"}
        </Title>
        <Subtitle marginTop="20px">
          {isMobile
            ? "유저를 사로잡고 클라이언트의 마음을\n훔치는 우리는 슬라임 입니다."
            : "유저를 사로잡고 클라이언트의 마음을 훔치는 우리는 슬라임 입니다."}
        </Subtitle>

        {/* <YouTube videoId="7TMtCgqOKP8" opts={{
          width: "640px",
          height: "320px",
          playerVars: {
            modestbranding: 1,
            autoplay: 1,
          },
        }} 
        onReady={(event) => {
          event.target.mute()
          event.target.playVideo();
        }}
        /> */}

        {/* <Image src="/images/typing_image.png" /> */}

        <video src="/videos/main.mp4"
          autoPlay
          loop
          playsInline
          muted
          style={{
            width: isMobile ? "80%" : "40%",
            height: "auto",
            marginTop: isMobile ? "30px" : "50px",
            borderRadius: "20px",
          }}
        />
        <Title marginTop="50px">
          {isMobile
            ? `온라인에서 당신의 서비스가\n주목받을수 있도록\n"슬라임"이 돕겠습니다.`
            : `온라인에서 당신의 서비스가 주목받을수 있도록\n "슬라임"이 돕겠습니다.`}
        </Title>
        <Subtitle marginTop="20px">
          {isMobile
            ? `저희는 흔한 대행사가 아닙니다. \n마케팅을 중심으로, 개발 구축 및 컨텐츠 제작을 하고 있는\n 종합솔루션 기업 주식회사 슬라임 입니다.`
            : `저희는 흔한 대행사가 아닙니다. 마케팅을 중심으로, 개발 구축 및
          컨텐츠 제작을 하고 있는 종합솔루션 기업 주식회사 슬라임 입니다.`}
        </Subtitle>
        <Title marginTop="100px">슬라임과 함께 하면?</Title>
        <Subtitle marginTop="20px">
          {isMobile
            ? `현장 경험이 많은 구성원들과,\n 젊은 에너지로 뭉친 슬라임! \n클라이언트의 성공을 위한 날개를 달아드립니다.`
            : `현장 경험이 많은 구성원들과, 젊은 에너지로 뭉친 슬라임! 클라이언트의 성공을 위한 날개를 달아드립니다.`}
        </Subtitle>

        <ButtonWrapper>
          <Button>마케팅 지원</Button>
          <Button>개발 지원</Button>
          <Button>컨텐츠 제작 지원</Button>
        </ButtonWrapper>

        <Title marginTop="120px">슬라임을 선택해야하는 이유!</Title>
        <Subtitle marginTop="10px">특별한 이유, 들어보실래요?</Subtitle>

        {/* 카드들 배치 */}
        <CardContainer>
          {cardData.map((card, index) => (
            <Card key={index} bgColor={card.bgColor} textColor={card.textColor}>
              {/* 이미지 대신 Icon을 img로 대체, 너비와 높이를 설정 */}
              <Icon
                src={card.imgSrc}
                alt="icon"
                width={card.imgWidth}
                height={card.imgHeight}
              />
              <Summary fontSize="17px" fontWeight="normal">
                {card.summaryText}
              </Summary>
              <Summary fontSize="22px" fontWeight="bold" marginTop="10px">
                {card.titleText}
              </Summary>
            </Card>
          ))}
        </CardContainer>
        <SpanWrapper>
          {isMobile ? (
            <>
              <CustomContainer
                mobileWidth="auto"
                mobileHeight="auto"
                mobileMargin="5px"
              >
                <SpanText fontSize="32px">
                  <BoldText>슬라임</BoldText>을 선택해주신{"\n"}
                  <BoldText>감사한 분들</BoldText> 입니다.
                </SpanText>
              </CustomContainer>
            </>
          ) : (
            <>
              <SpanText fontSize="32px">
                <BoldText>슬라임</BoldText>을 선택해주신
                <BoldText>감사한 분들</BoldText> 입니다.
              </SpanText>
            </>
          )}
        </SpanWrapper>
        {isMobile ? (
          <MobileCompanyList></MobileCompanyList>
        ) : (
          <>
            <CustomContainer flexDirection="column">
              <CustomContainer flexDirection="row" gap="30px" height="80px">
                {companyIcons1.map((src, index) => (
                  <Icon key={index} src={src} />
                ))}
              </CustomContainer>
              <CustomContainer flexDirection="row" gap="30px" height="80px">
                {companyIcons2.map((src, index) => (
                  <Icon key={index} src={src} />
                ))}
              </CustomContainer>
              <CustomContainer flexDirection="row" gap="30px" height="80px">
                {companyIcons3.map((src, index) => (
                  <Icon key={index} src={src} />
                ))}
              </CustomContainer>
            </CustomContainer>
          </>
        )}

        <Title marginTop="100px">슬라임은 2021년부터 성장하고,</Title>
        <TitleLight marginTop="10px">지속적인 성장을 하고 있습니다.</TitleLight>
        <Marquee speed={100} gradient={false} pauseOnHover>
          <HorizontalCardWrapper>
            {extendedData.map((timeline, index) => (
              <HorizontalCard key={index}>
                <DateWrapper>{timeline.date}</DateWrapper>
                {timeline.items.map((item, idx) => (
                  <LineItem key={idx}>
                    <CheckIcon
                      src="/images/company_icon/check.png"
                      alt="check"
                    />
                    <LineText>{item}</LineText>
                  </LineItem>
                ))}
              </HorizontalCard>
            ))}
          </HorizontalCardWrapper>
        </Marquee>
        <InquireComponent isMobile={isMobile} />
      </IntroTextWrapper>
    </Container>
  );
}

export default Company;
